/* eslint-disable react/no-unescaped-entities, react-hooks/immutability */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Sparkles, RefreshCw, FileDown, Activity, AlertTriangle, Target,
    TrendingUp, Award, Users, Clock, Euro, ArrowLeft, CheckCircle2,
    Shield, Zap, BarChart3, Gauge, AlertCircle, Mail
} from 'lucide-react';

const PURPLE = '#681793';
const ORANGE = '#FF914D';

const ICON_MAP = {
    Target, TrendingUp, Award, Users, Clock, Euro, Activity, AlertTriangle,
    Sparkles, Shield, Zap, BarChart3, Gauge, AlertCircle, CheckCircle2,
};

const PRIORITY_COLORS = {
    high: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', label: 'Priorité haute' },
    medium: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', label: 'Priorité moyenne' },
    low: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Priorité faible' },
};

const SECTION_ICONS = {
    'Acquisition & Lead generation': Activity,
    'Estimation & Qualification': Award,
    'Mandats & Exclusivité': Shield,
    'Visites, Offres & Négociation': Users,
    'Compromis, Acte & Rentabilité': Euro,
    'Performance Agents & Transformation': Gauge,
};

function fmt(value, unit) {
    if (value === null || value === undefined) return '—';
    if (unit === 'pct') return `${value}%`;
    if (unit === 'currency') {
        if (typeof value === 'number' && value >= 1000) return `${(value / 1000).toFixed(1)}k€`;
        return `${value}€`;
    }
    if (unit === 'days') return `${value}j`;
    if (unit === 'hours') return `${value}h`;
    if (unit === 'ratio') return `x${value}`;
    return String(value);
}

function healthGradient(score) {
    if (score >= 80) return 'from-emerald-500 to-emerald-400';
    if (score >= 60) return 'from-[#FF914D] to-amber-400';
    if (score >= 40) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-500';
}

function KpiBar({ kpi, max }) {
    const v = typeof kpi.value === 'number' ? kpi.value : 0;
    const pct = max > 0 ? Math.min(100, (v / max) * 100) : 0;
    return (
        <div className="flex items-center gap-3 py-1.5" data-testid={`report-kpi-${kpi.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm text-foreground/90 truncate">{kpi.name}</span>
                    <span className="text-sm font-bold text-foreground tabular-nums">{fmt(kpi.value, kpi.unit)}</span>
                </div>
                <div className="h-1.5 mt-1 rounded-full bg-white/5 overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className="h-full rounded-full"
                        style={{ background: `linear-gradient(90deg, ${PURPLE}, ${ORANGE})` }}
                    />
                </div>
            </div>
        </div>
    );
}

function SectionCard({ section, index }) {
    const Icon = SECTION_ICONS[section.title] || BarChart3;
    const numericKpis = (section.top_kpis || []).filter(k => typeof k.value === 'number');
    const maxVal = numericKpis.length ? Math.max(...numericKpis.map(k => Math.abs(k.value))) : 1;

    return (
        <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08 }}
            className="bg-card border border-border rounded-2xl p-5 md:p-6"
            data-testid={`report-section-${index}`}
        >
            <div className="flex items-start gap-4 mb-4">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/20 flex items-center justify-center border border-[#681793]/30">
                    <Icon className="w-5 h-5 text-[#FF914D]" />
                </div>
                <div className="flex-1">
                    <h2 className="text-lg md:text-xl font-bold text-foreground">{section.title}</h2>
                    {section.summary && (
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{section.summary}</p>
                    )}
                </div>
            </div>

            {section.key_findings?.length > 0 && (
                <ul className="space-y-2 mb-5">
                    {section.key_findings.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground/85">
                            <CheckCircle2 className="w-4 h-4 text-[#FF914D] mt-0.5 shrink-0" />
                            <span className="leading-relaxed">{f}</span>
                        </li>
                    ))}
                </ul>
            )}

            {numericKpis.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-semibold">KPIs clés</p>
                    {numericKpis.map(k => <KpiBar key={k.id} kpi={k} max={maxVal} />)}
                </div>
            )}
        </motion.section>
    );
}

function RecommendationCard({ reco, index }) {
    const colors = PRIORITY_COLORS[reco.priority] || PRIORITY_COLORS.medium;
    const Icon = ICON_MAP[reco.icon] || Target;
    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.06 }}
            className={`rounded-xl border ${colors.bg} ${colors.border} p-4 flex gap-3 items-start`}
            data-testid={`report-reco-${index}`}
        >
            <div className={`w-10 h-10 shrink-0 rounded-lg ${colors.bg} ${colors.border} border flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${colors.text}`} />
            </div>
            <div className="flex-1">
                <div className={`text-[10px] uppercase tracking-wider font-bold ${colors.text} mb-1`}>{colors.label}</div>
                <h3 className="font-bold text-foreground text-sm">{reco.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{reco.detail}</p>
                {reco.impact && (
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#681793]/15 border border-[#681793]/30">
                        <TrendingUp className="w-3 h-3 text-[#FF914D]" />
                        <span className="text-xs font-semibold text-foreground">{reco.impact}</span>
                    </div>
                )}
            </div>
        </motion.div>
    );
}

export default function AIFullReportPage() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const pollRef = useRef(null);

    const loadReport = useCallback(async (force = false, isRetry = false) => {
        try {
            const res = await api.get(`/cockpit-ai/full-report${force ? '?force=true' : ''}`);
            setReport(res.data);
            return res.data;
        } catch (err) {
            const status = err?.response?.status;
            // 502/503/504 (ou pas de réponse) = lenteur passagère au démarrage : on retente 1 fois
            if (!isRetry && (!status || [502, 503, 504].includes(status))) {
                await new Promise((r) => setTimeout(r, 1800));
                return loadReport(force, true);
            }
            toast.error('Impossible de charger le rapport');
            return null;
        }
    }, []);

    useEffect(() => {
        loadReport().finally(() => setLoading(false));
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [loadReport]);

    // Polling tant qu'un rapport est en génération
    useEffect(() => {
        if (!report?.is_generating) {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            return;
        }
        if (pollRef.current) return;
        pollRef.current = setInterval(async () => {
            const data = await loadReport();
            if (data && !data.is_generating) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setRefreshing(false);
                toast.success('Rapport mis à jour');
            }
        }, 10000);
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [report?.is_generating, loadReport]);

    const handleRefresh = async () => {
        setRefreshing(true);
        toast.info('Régénération du rapport en cours (60-90s)…');
        await loadReport(true);
    };

    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            const res = await api.get('/cockpit-ai/full-report/pdf', { responseType: 'blob' });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `homly-rapport-ia-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF téléchargé');
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || 'Erreur lors du téléchargement du PDF';
            if (status === 429) toast.warning(detail);
            else toast.error(detail);
        } finally {
            setDownloading(false);
        }
    };

    const [emailing, setEmailing] = useState(false);
    const handleEmailReport = async () => {
        setEmailing(true);
        try {
            const res = await api.post('/cockpit-ai/full-report/email');
            const d = res?.data || {};
            if (d.mocked) {
                toast.info('Email envoyé (mode test, Resend non configuré)');
            } else {
                toast.success(`Rapport envoyé à ${d.to_email}`);
            }
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || 'Échec de l\'envoi du rapport';
            if (status === 429) toast.warning(detail);
            else toast.error(detail);
        } finally {
            setEmailing(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto space-y-4 p-4">
                <Skeleton className="h-32 w-full rounded-2xl bg-muted" />
                <div className="grid md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-48 rounded-2xl bg-muted" />)}
                </div>
            </div>
        );
    }

    if (!report) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="ai-full-report-error">
                <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8" data-testid="back-to-dashboard">
                    <ArrowLeft className="w-4 h-4" /> Retour au cockpit
                </Link>
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8">
                    <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-foreground mb-2">Rapport momentanément indisponible</h2>
                    <p className="text-sm text-muted-foreground mb-6">
                        La génération a pris trop de temps ou le service IA était occupé. Réessayez dans quelques secondes — votre analyse sera affichée.
                    </p>
                    <Button
                        onClick={() => { setLoading(true); loadReport().finally(() => setLoading(false)); }}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white"
                        data-testid="retry-report-btn"
                    >
                        <RefreshCw className="w-4 h-4 mr-1.5" /> Réessayer
                    </Button>
                </div>
            </div>
        );
    }

    const isGenerating = report.is_generating;
    const isStale = report.stale;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-6xl mx-auto pb-12"
            data-testid="ai-full-report-page"
        >
            {/* Back link */}
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4" data-testid="back-to-dashboard">
                <ArrowLeft className="w-4 h-4" /> Retour au cockpit
            </Link>

            {/* HERO */}
            <motion.div
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#681793]/30 via-card to-[#FF914D]/15 border border-[#681793]/30 p-6 md:p-8 mb-8"
            >
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#681793]/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF914D]/15 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" />

                <div className="relative">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#681793]/30 border border-[#681793]/40 mb-3">
                                <Sparkles className="w-3.5 h-3.5 text-[#FF914D]" />
                                <span className="text-xs font-semibold text-foreground">Rapport IA approfondi · Pilote (IA Homly)</span>
                            </div>
                            <h1 className="text-2xl md:text-4xl font-black text-foreground leading-tight">{report.headline}</h1>
                            <p className="text-sm text-muted-foreground mt-1">{report.week_label}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={refreshing || isGenerating}
                                className="border-[#681793]/40 hover:bg-[#681793]/10"
                                data-testid="refresh-report-btn"
                            >
                                <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing || isGenerating ? 'animate-spin' : ''}`} />
                                {refreshing || isGenerating ? 'Génération…' : 'Régénérer'}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleEmailReport}
                                disabled={emailing || downloading || isGenerating || !report.sections?.length}
                                className="border-[#681793]/40 hover:bg-[#681793]/10"
                                data-testid="email-report-btn"
                            >
                                <Mail className={`w-4 h-4 mr-1.5 ${emailing ? 'animate-pulse' : ''}`} />
                                {emailing ? 'Envoi…' : 'Envoyer par email'}
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleDownloadPdf}
                                disabled={downloading || isGenerating || !report.sections?.length}
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white"
                                data-testid="download-pdf-btn"
                            >
                                <FileDown className={`w-4 h-4 mr-1.5 ${downloading ? 'animate-pulse' : ''}`} />
                                {downloading ? 'Préparation…' : 'Exporter en PDF'}
                            </Button>
                        </div>
                    </div>

                    {/* Banner if stale */}
                    {isStale && isGenerating && (
                        <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center gap-2.5">
                            <RefreshCw className="w-4 h-4 text-amber-400 animate-spin" />
                            <span className="text-sm text-amber-300">Nouvelle version en cours de génération. Cette page se mettra à jour automatiquement.</span>
                        </div>
                    )}

                    {isGenerating && !isStale && (
                        <div className="my-4 p-4 rounded-xl bg-[#681793]/10 border border-[#681793]/30 flex items-center gap-3">
                            <RefreshCw className="w-5 h-5 text-[#FF914D] animate-spin shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-foreground">L'IA analyse vos KPIs…</p>
                                <p className="text-xs text-muted-foreground mt-0.5">60-90 secondes en moyenne. La page se rafraîchira automatiquement.</p>
                            </div>
                        </div>
                    )}

                    {/* Health score gauge */}
                    {report.health_score !== undefined && !isGenerating && (
                        <div className="bg-background/30 backdrop-blur-sm rounded-2xl border border-white/5 p-5 mt-4">
                            <div className="flex items-baseline justify-between mb-2">
                                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Score santé de l'agence</span>
                                <span className="text-3xl font-black text-foreground">{report.health_score}<span className="text-base text-muted-foreground">/100</span></span>
                            </div>
                            <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${report.health_score}%` }}
                                    transition={{ duration: 1, ease: 'easeOut', delay: 0.3 }}
                                    className={`h-full rounded-full bg-gradient-to-r ${healthGradient(report.health_score)}`}
                                />
                            </div>
                            <p className="text-sm text-foreground/80 mt-2 font-semibold">{report.health_label}</p>
                        </div>
                    )}

                    {/* Executive summary */}
                    {report.executive_summary && (
                        <div className="mt-5 pt-5 border-t border-white/10">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Synthèse exécutive</p>
                            <p className="text-foreground/90 leading-relaxed text-sm md:text-base">{report.executive_summary}</p>
                        </div>
                    )}

                    {/* KPI totals */}
                    {report.kpi_totals && (
                        <div className="mt-5 flex flex-wrap gap-2">
                            <div className="px-3 py-1.5 rounded-full bg-[#681793]/15 border border-[#681793]/30 text-xs">
                                <span className="font-bold text-foreground">{report.kpi_totals.live}</span>
                                <span className="text-muted-foreground"> KPIs croisés sur {report.kpi_totals.total}</span>
                            </div>
                            <div className="px-3 py-1.5 rounded-full bg-card border border-border text-xs text-muted-foreground">
                                Source : {report.source === 'ai' ? 'Analyse IA Pilote (Homly)' : report.source === 'computing' ? 'Calcul en cours' : 'Analyse locale'}
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* SECTIONS */}
            <AnimatePresence>
                {report.sections?.length > 0 && (
                    <div className="grid lg:grid-cols-2 gap-5 mb-8">
                        {report.sections.map((s, i) => <SectionCard key={i} section={s} index={i} />)}
                    </div>
                )}
            </AnimatePresence>

            {/* RECOMMANDATIONS */}
            {report.recommendations?.length > 0 && (
                <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8" data-testid="report-recommendations">
                    <div className="flex items-center gap-2 mb-4">
                        <Target className="w-5 h-5 text-[#FF914D]" />
                        <h2 className="text-xl md:text-2xl font-bold text-foreground">Recommandations prioritaires</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">Actions à fort impact identifiées par l'IA, ordonnées par priorité.</p>
                    <div className="grid md:grid-cols-2 gap-3">
                        {report.recommendations.map((r, i) => <RecommendationCard key={i} reco={r} index={i} />)}
                    </div>
                </motion.section>
            )}

            {/* RISQUES */}
            {report.risks?.length > 0 && (
                <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="report-risks">
                    <div className="flex items-center gap-2 mb-4">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                        <h2 className="text-xl md:text-2xl font-bold text-foreground">Risques détectés</h2>
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-5">
                        <ul className="space-y-2.5">
                            {report.risks.map((r, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/90">
                                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                                    <span className="leading-relaxed">{r}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </motion.section>
            )}
        </motion.div>
    );
}
